import { useContext, useState } from "react"
import { Flex, Text, Menu, Checkbox, Box, ScrollArea, UnstyledButton, Group, TextInput, Highlight, Tooltip } from "@mantine/core";
import { IconChevronDown, IconSearch, IconX } from "@tabler/icons";
import SingleCustomerContext from "../Customer-Context/SingleCustomerContext";
import { LIGHT_BLUE_BACKGROUND, PRIMARY_OPACITY_12 } from "../../../constants/colors";
import { capitalizeFirstLetter } from "../../../utils/capitalizeFirstLetter";

const FilterBar = () => {

  const { activeFilters, setActiveFilters, ideasFiltersConfig } = useContext(SingleCustomerContext)
  const [selectedValues, setSelectedValues] = useState<Record<string, string[]>>({});
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const accountFilters = ideasFiltersConfig?.evidence?.account?.childFilters
  const evidenceFilters = ideasFiltersConfig.evidence
 
  // Concatenate all childFilters from all evidence filters into one array
  const allChildFilters = Object.values(evidenceFilters)
    .filter((evidenceFilter: any) => evidenceFilter?.childFilters)
    .flatMap((evidenceFilter: any) => Object.values(evidenceFilter.childFilters));
 
  const accountFiltersArray = allChildFilters ? Object.values(allChildFilters).filter((filter: any) => filter.showIn360 === true) : [];
  
  const handleCheckboxChange = (filterName: string, values: string[], filterItem: any) => {

    const dbFieldPath = filterItem.dbFieldPath.includes('.') 
      ? filterItem.dbFieldPath.substring(filterItem.dbFieldPath.indexOf('.') + 1)
      : filterItem.dbFieldPath;

    // Update selectedValues state
    setSelectedValues(prev => ({
      /* ...filterItem, */
      ...prev,
      dbFieldPath,
      [filterName]: values
    }));

    // Check if filter type exists in activeFilters
    const existingFilterIndex = activeFilters.findIndex(
      (f: any) => f.type === (filterName === 'account-types' ? 'type' : filterName)
    );

    // Get current values for this filter
    const currentValues = selectedValues[filterName] || [];
    
    if (currentValues.length === 1 && values.length === 0) {
      // If we're unchecking the last remaining item, remove the filter type
      setActiveFilters((prev: any[]) => 
        prev.filter((_, index) => index !== existingFilterIndex)
      );
    } else if (existingFilterIndex === -1) {
      // If filter type doesn't exist, add new filter
      setActiveFilters((prev: any[]) => [
        ...prev,
        
        { 
          dbFieldPath,
          type: filterName === 'account-types' ? 'type' : filterName,
          values: values
        }
      ]);
    } else {
      // If filter type exists, update its values
      setActiveFilters((prev: any[]) => 
        prev.map((filter, index) => 
          index === existingFilterIndex 
            ? { ...filter, values: values, dbFieldPath: dbFieldPath, }
            : filter
        )
      );
    }
  };

  const clearFilter = (filterName: string) => {
    // Clear from selectedValues
    setSelectedValues(prev => ({
      ...prev,
      [filterName]: []
    }));

    // Remove from activeFilters
    setActiveFilters((prev: any[]) => 
      prev.filter((f: any) => f.type !== (filterName === 'account-types' ? 'type' : filterName))
    );
  };

  const handleSearchChange = (filterName: string, value: string) => {
    setSearchQueries(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const getFilteredOptions = (filter: any) => {
    const searchQuery = searchQueries[filter.name]?.toLowerCase() || '';
    return filter?.options?.filter((option: any) => 
      (option.label || option.value).toLowerCase().includes(searchQuery)
    );
  };

  return (
    <Flex gap={8} wrap="wrap">{accountFiltersArray && 
      accountFiltersArray.length > 0 && 
      accountFiltersArray.map((filter: any, index: number) => (
        <Menu 
          key={index} 
          position="bottom-start" 
          withArrow
          closeOnClickOutside={true}
          closeOnItemClick={false}
          width={300}
        >
          <Menu.Target>
            <Flex 
              bg={selectedValues[filter.name]?.length > 0 ? LIGHT_BLUE_BACKGROUND : 'white'}
              h={32}
              sx={{
                border: '1px solid #d8d8d8',
                padding: '4px 8px',
                borderRadius: 4,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor:selectedValues[filter.name]?.length > 0 ? PRIMARY_OPACITY_12 :'#f8f9fa'
                }
              }}
              justify="space-between"
              align="center"
            >
              <Tooltip label={filter?.label} withinPortal>
              
                <Text fz={12} lineClamp={1}>{filter?.label}</Text>
              
              </Tooltip>
              
             
              {selectedValues[filter.name]?.length > 0 && <Text fz={12}>:</Text>}

              {selectedValues[filter.name]?.length > 0 &&
              <Tooltip withinPortal label={selectedValues[filter.name].map(value => capitalizeFirstLetter(value)).join(', ')}>
                <Flex>
                  <Text lineClamp={1} maw={100} fz={12} ml={6}>
                    {selectedValues[filter.name]?.length > 0 ?
                      capitalizeFirstLetter(selectedValues[filter.name][0]) :
                      ''}
                  </Text>
                </Flex>
              </Tooltip>
              }
              
              <Text lineClamp={1} maw={100} fz={12}>
                {selectedValues[filter.name]?.length > 1 ?
                  `+${selectedValues[filter.name].length - 1}` 
                  : ''
                }</Text>
              <IconChevronDown size={16} color="#4F4F4F" fill="#4F4F4F" stroke={2} />
            </Flex>
          </Menu.Target>
          <Menu.Dropdown>
            <Box>
              <TextInput
                placeholder={`Search ${filter.label}`}
                value={searchQueries[filter.name] || ''}
                onChange={(e) => handleSearchChange(filter.name, e.target.value)}
                rightSection={<IconSearch size={14} />}
                mb={8}
              />
              <ScrollArea h={220} mah={220}>
                <Box w={220}>
                  <Checkbox.Group
                    value={selectedValues[filter.name] || []}
                    onChange={(values) => handleCheckboxChange(filter.name, values, filter)}
                  >
                    {getFilteredOptions(filter)?.map((option: any, optionIndex: number) => (
                      <Menu.Item key={optionIndex} p={8}>
                        <Checkbox
                          value={option.value}
                          label={
                            <Tooltip 
                              maw={250} 
                              multiline 
                              withinPortal 
                              label={capitalizeFirstLetter(option.label || option.value)}>
                              <Text 
                                fz={12}
                                lineClamp={1}
                                maw={220}
                              >
                                <Highlight
                                  highlight={searchQueries[filter.name] || ''}
                                  highlightStyles={(theme) => ({
                                    backgroundColor: theme.colors.yellow[2],
                                    fontWeight: 700,
                                    padding: 0,
                                  })}
                                >
                                  {capitalizeFirstLetter(option.label || option.value)}
                                </Highlight>
                              </Text>
                            </Tooltip>
                          }
                          size="xs"
                          color="indigo"
                        />
                      </Menu.Item>
                    ))}
                  </Checkbox.Group>
                </Box>
              </ScrollArea>
              <Box w='100%'>
                <Box
                  sx={{
                    backgroundColor: "#F8F9FA",
                    borderRadius: "0 0 8px 8px",
                    height: 48,
                    alignContent: "center",
                    borderTop: '1px solid #eee',
                  }}
                  pl="md"
                  pr={8}
                  py={8}
                >
               
                  <UnstyledButton onClick={() => clearFilter(filter.name)}>
                    <Group
                      spacing={5}
                      sx={{
                        color: "#5C5CEB",
                      }}
                    >
                      <IconX size={13} />
                      <Text size={13}>Clear filter</Text>
                    </Group>
                  </UnstyledButton>
                </Box>
              </Box>
              
            </Box>
          </Menu.Dropdown>
        </Menu>
      ))}</Flex>
  )
}

export default FilterBar